import clsx from "clsx"
import moment from "moment"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"
import TableCell from "@material-ui/core/TableCell"
import Checkbox from "@material-ui/core/Checkbox"
import Box from "@material-ui/core/Box"
import Button from "@material-ui/core/Button"
import Select from "@material-ui/core/Select"
import MenuItem from "@material-ui/core/MenuItem"
import Autocomplete from "@material-ui/lab/Autocomplete"
import TextField from "@material-ui/core/TextField"
import { makeStyles } from "@material-ui/core/styles"

import AddIcon from "@material-ui/icons/Add"
import ClearIcon from "@material-ui/icons/Clear"

import { SearchTextField } from "components/partials"
import { ActionCreators, useAdminState, useAdminDispatch } from "../Context"
import { AdminHeaderForm } from "components/partials"
import { useAdminFilter, useIsInputWritable } from "common/hooks"
import {
    getFormTitle,
    getFilterPlaceholder
} from "common/helpers/admin-page-func.helper"
import { CompaniesApi } from "common/api"
import { useAppSelector } from "common/hooks"
import { Role } from "common/types"
import { HelpTooltip } from "components/widgets"
import { getCompanyAvailableLicenses } from "../FormDialog/Form"

const useStyles = makeStyles(theme => ({
    // https://stackoverflow.com/a/11267268/8062659
    cellFit: {
        width: "1%",
        whiteSpace: "nowrap"
    },

    grey: {
        color: theme.palette.grey[400]
    }
}))

const DATE_FORMAT = "MM/DD/YYYY"

const Header = props => {
    const classes = useStyles()
    const { t } = useTranslation()
    const { rows } = useAdminState()
    const dispatch = useAdminDispatch()
    const [filter, onChangeFilter] = useAdminFilter()
    const [companies, setCompanies] = useState([])

    const { user: currentUser } = useAppSelector(state => state.auth)
    const currentUserRole = Role[currentUser.role]
    const disabled = currentUserRole < Role.Superadmin

    const isWritable = useIsInputWritable()

    const lastPublishAtFilters = [
        {
            label: t("view.adminCatalogs.pastNDays", { days: 7 }),
            value: moment()
                .subtract(7, "days")
                .format(DATE_FORMAT)
        },
        {
            label: t("view.adminCatalogs.pastNDays", { days: 30 }),
            value: moment()
                .subtract(30, "days")
                .format(DATE_FORMAT)
        },
        {
            label: t("view.adminCatalogs.pastNDays", { days: 90 }),
            value: moment()
                .subtract(90, "days")
                .format(DATE_FORMAT)
        }
    ]

    useEffect(() => {
        CompaniesApi.get().then(response => {
            setCompanies(response.data)
        })
    }, [])

    function onToggleSelectAll(event) {
        const { checked } = event.target
        dispatch(
            checked ? ActionCreators.selectAll() : ActionCreators.deselectAll()
        )
    }

    function onClickCreate() {
        let selectedCompany = undefined
        if (
            currentUserCompany &&
            getCompanyAvailableLicenses(currentUserCompany) > 0
        ) {
            selectedCompany = currentUserCompany
        }

        dispatch(
            ActionCreators.setToEdit({
                name: "",
                enabled: true,
                company: selectedCompany
            })
        )
    }

    function onChangeCompany(event, value) {
        if (!event) return // Fix for clearing all filters

        const target = { ...event.target, value }
        onChangeFilter("company")({
            ...event,
            target
        })
    }

    const totalSelected = rows.filter(({ isSelected }) => isSelected).length
    const totalRows = rows.length

    const currentUserCompany = companies.find(
        company => company.id === currentUser?.company?.id
    )
    let canCreateCatalog = false
    if (currentUserCompany && !disabled) {
        const {
            unlimitedCatalogLicenses,
            catalogLicenses,
            totalOwnedCatalogs
        } = currentUserCompany
        if (unlimitedCatalogLicenses) {
            canCreateCatalog = true
        } else {
            canCreateCatalog = catalogLicenses >= totalOwnedCatalogs + 1
        }
    }

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        disabled={disabled}
                        indeterminate={totalSelected > 0 && totalSelected < totalRows}
                        checked={totalRows > 0 && totalSelected === totalRows}
                        onChange={onToggleSelectAll}
                    />
                </TableCell>

                <TableCell>
                    <AdminHeaderForm
                        label={t("view.adminCatalogs.filterId")}
                        SortProps={{
                            key: "name"
                        }}
                    >
                        <SearchTextField
                            fullWidth
                            placeholder={getFilterPlaceholder(
                                t("view.adminCatalogs.filterId")
                            )}
                            value={filter.name || ""}
                            onChange={onChangeFilter("name")}
                        />
                    </AdminHeaderForm>
                </TableCell>

                <TableCell>
                    <AdminHeaderForm
                        label={t("view.adminCatalogs.filterTitle")}
                        SortProps={{
                            key: "title"
                        }}
                    >
                        <SearchTextField
                            fullWidth
                            placeholder={getFilterPlaceholder(
                                t("view.adminCatalogs.filterTitle")
                            )}
                            value={filter.title || ""}
                            onChange={onChangeFilter("title")}
                        />
                    </AdminHeaderForm>
                </TableCell>

                <TableCell style={{ minWidth: 200, width: "1%", whiteSpace: "nowrap" }}>
                    <AdminHeaderForm
                        label={t("view.adminCatalogs.filterLastPublished")}
                        SortProps={{
                            key: "lastPublishAt"
                        }}
                    >
                        <Select
                            fullWidth
                            displayEmpty
                            defaultValue=""
                            onChange={onChangeFilter("lastPublishAt")}
                            className={clsx({ [classes.grey]: !filter.lastPublishAt })}
                        >
                            <MenuItem value="" selected>
                                {getFilterPlaceholder(
                                    t("view.adminCatalogs.filterLastPublished")
                                )}
                            </MenuItem>
                            {lastPublishAtFilters.map((filter, index) => (
                                <MenuItem key={index} value={filter.value}>
                                    {filter.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </AdminHeaderForm>
                </TableCell>

                {props.showCompany && (
                    <TableCell>
                        <AdminHeaderForm
                            label={t("view.admin.company")}
                            SortProps={{
                                key: "company"
                            }}
                        >
                            <Autocomplete
                                freeSolo
                                style={{ minWidth: 150 }}
                                options={[
                                    "Unassigned",
                                    ...companies.map(company => company.name)
                                ]}
                                value={filter.company || ""}
                                onInputChange={onChangeCompany}
                                closeIcon={<ClearIcon color="primary" />}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        placeholder={getFilterPlaceholder(t("view.admin.company"))}
                                        InputProps={{ ...params.InputProps }}
                                    />
                                )}
                            />
                        </AdminHeaderForm>
                    </TableCell>
                )}

                <TableCell style={{ minWidth: 200, width: "1%", whiteSpace: "nowrap" }}>
                    <AdminHeaderForm
                        label={t("view.adminCatalogs.filterEnabled")}
                        SortProps={{
                            key: "enabled"
                        }}
                    >
                        <Select
                            fullWidth
                            displayEmpty
                            value={filter.enabled || ""}
                            onChange={onChangeFilter("enabled")}
                            className={clsx({ [classes.grey]: !filter.enabled })}
                        >
                            <MenuItem value="" selected>
                                {getFilterPlaceholder(t("view.adminCatalogs.filterEnabled"))}
                            </MenuItem>
                            <MenuItem value="true">True</MenuItem>
                            <MenuItem value="false">False</MenuItem>
                        </Select>
                    </AdminHeaderForm>
                </TableCell>

                <TableCell className={classes.cellFit}>
                    {isWritable(Role.Moderator) ? (
                        <Box display="flex" flexDirection="column" alignItems="flex-start">
                            {t("view.admin.actions")}

                            <Box display="flex" alignItems="center" style={{ columnGap: 8 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={onClickCreate}
                                    disabled={
                                        !canCreateCatalog || currentUserRole < Role.Superadmin
                                    }
                                >
                                    {getFormTitle(t("view.admin.catalog"))}
                                </Button>
                                {currentUserRole < Role.Superadmin && (
                                    <HelpTooltip
                                        title={t(
                                            "view.adminCatalogs.onlyAllowedForSuperAdminsTooltip"
                                        )}
                                    />
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Box textAlign="right">{t("view.admin.actions")}</Box>
                    )}
                </TableCell>
            </TableRow>
        </TableHead>
    )
}

export default Header
